// Shared context-usage UI primitives.
//
// PR-55 / PR-58 introduced the desktop chat-header chip; this file
// extracts the calculation + rendering so the mobile chat can reuse
// the same numbers without duplicating the threshold/colour logic.
//
// Exports:
//   - computeUsageStats(usage)     — pure, testable derivation
//   - TokenUsageChip               — desktop chip + click-to-popover
//   - MobileTokenUsageChip         — phone chip + tap-to-bottom-sheet
//   - UsageRow                     — shared key/value row
//
// The desktop chip is the verbatim move from DiscussionPane.tsx; the
// mobile variant swaps the floating popover for the existing
// .mobile-action-sheet pattern so the breakdown isn't clipped on
// narrow widths.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ThreadUsage } from '../api';

interface UsageStats {
  window: number;
  sys: number;
  tools: number;
  postMsgs: number;
  preMsgs: number;
  threshold: number;
  actualInput: number;
  pctOfWindow: number;
  thresholdAsTotal: number;
  thresholdPct: number | null;
  tier: 'calm' | 'warn' | 'hot';
  accent: string;
  labelColor: string;
}

/** Pure derivation. Defensive defaults handle legacy snapshots that
 *  haven't populated the new {pre,post}_compact / sys / tools fields. */
export function computeUsageStats(usage: ThreadUsage): UsageStats {
  const window = Math.max(usage.context_window || 200_000, 1);
  const sys = usage.system_tokens || 0;
  const tools = usage.tools_tokens || 0;
  const postMsgs =
    usage.post_compact_messages || usage.estimated_next_input || 0;
  const preMsgs = usage.pre_compact_messages || postMsgs;
  const threshold = usage.compact_threshold || 0;

  const actualInput = sys + tools + postMsgs;
  const pctOfWindow = Math.min(100, Math.round((actualInput / window) * 100));

  const thresholdAsTotal = sys + tools + threshold;
  const thresholdPct =
    threshold > 0
      ? Math.min(100, Math.round((thresholdAsTotal / window) * 100))
      : null;

  // Tier keyed off proximity to compaction (NOT to window cap) so 1M
  // models still warn at 80% of the compaction line — long before the
  // bar would visibly fill.
  const distanceToCompact =
    threshold > 0 ? actualInput / thresholdAsTotal : actualInput / window;
  const tier: 'calm' | 'warn' | 'hot' =
    distanceToCompact < 0.7 ? 'calm' : distanceToCompact < 0.9 ? 'warn' : 'hot';
  const accent =
    tier === 'calm' ? 'var(--ink-2)' : tier === 'warn' ? '#c97a16' : '#b8431a';
  const labelColor = tier === 'calm' ? 'var(--ink-3)' : accent;

  return {
    window,
    sys,
    tools,
    postMsgs,
    preMsgs,
    threshold,
    actualInput,
    pctOfWindow,
    thresholdAsTotal,
    thresholdPct,
    tier,
    accent,
    labelColor,
  };
}

export function formatTokens(n: number): string {
  return n >= 1000
    ? `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}K`
    : `${n}`;
}

/**
 * PR-55 / PR-58 desktop chip: compact pill in the chat header. Click to
 * open a popover with the full breakdown. Mirrors Claude Code's
 * "Context window 211.6k / 1.0M (21%)" affordance.
 *
 * What the chip shows:
 *   - Numerator = sys + tools + post-compact messages (= what the LLM
 *     actually receives next turn). This includes the ~20K of
 *     overhead the user can't see in the chat (system prompt + tools
 *     schema), so the chip never lies about real budget usage.
 *   - Denominator = the active model's physical context window
 *     (200K for GLM, 1M for DeepSeek v4 tiers).
 *
 * Compaction line: a tick mark on the progress bar at the
 * `compact_threshold` (scaled per model). Crossing it triggers a
 * server-side template summarisation that collapses older messages.
 * Color tier reflects how close the chip is to compaction:
 *   - <70% : ink-2 (calm) — chip stays neutral
 *   - 70–90% : amber — getting close
 *   - >90% : red — auto-compact will fire next turn
 *
 * The popover surfaces the pre/post compaction split so users
 * understand WHY a chip ever shows "compacted" — they can see "would
 * have weighed N K, after compact = M K".
 */
export function TokenUsageChip({ usage }: { usage: ThreadUsage }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const stats = computeUsageStats(usage);
  const {
    window,
    actualInput,
    pctOfWindow,
    accent,
    labelColor,
  } = stats;

  return (
    <div ref={wrapperRef} style={{ position: 'relative', marginLeft: 4 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={t('usage.chipTitle', {
          input: actualInput.toLocaleString(),
          window: window.toLocaleString(),
          pct: pctOfWindow,
        })}
        className="mono"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          padding: '2px 7px',
          fontSize: 10,
          color: labelColor,
          background: open ? 'var(--paper-1, #fafaf6)' : 'transparent',
          border: '1px solid var(--rule)',
          borderRadius: 10,
          cursor: 'pointer',
          letterSpacing: '.04em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: accent,
          }}
        />
        {pctOfWindow}%
        {usage.compact_triggered && (
          <span
            aria-label={t('usage.compactionActive')}
            title={t('usage.compactedThisTurn', {
              count: usage.compacted_message_count,
            })}
            style={{ fontSize: 9 }}
          >
            📦
          </span>
        )}
      </button>
      {open && (
        <div
          role="dialog"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 10,
            background: 'white',
            border: '1px solid var(--rule)',
            borderRadius: 6,
            boxShadow: '0 6px 20px rgba(0,0,0,0.10)',
            padding: '12px 14px',
            minWidth: 280,
          }}
        >
          <UsageBreakdown usage={usage} stats={stats} />
        </div>
      )}
    </div>
  );
}

/**
 * Mobile chip: same chip + same numbers, but tap opens a
 * .mobile-action-sheet (bottom sheet) instead of a floating popover.
 * The popover would clip on narrow widths.
 */
export function MobileTokenUsageChip({ usage }: { usage: ThreadUsage }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const stats = computeUsageStats(usage);
  const { actualInput, window, pctOfWindow, accent, labelColor } = stats;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('usage.mobileChipAria', { pct: pctOfWindow })}
        className="mono mobile-context-chip"
        style={{
          color: labelColor,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span
          aria-hidden
          className="mobile-context-chip__dot"
          style={{ background: accent }}
        />
        {pctOfWindow}%
        {usage.compact_triggered && (
          <span aria-hidden style={{ fontSize: 10, marginLeft: 2 }}>
            📦
          </span>
        )}
      </button>
      {open && (
        <div
          className="mobile-action-sheet-backdrop"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-label={t('usage.mobileSheetAria')}
        >
          <div
            className="mobile-action-sheet mobile-action-sheet--scrollable"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mobile-action-sheet__title">
              {t('usage.mobileSheetTitle', {
                input: actualInput.toLocaleString(),
                window: window.toLocaleString(),
                pct: pctOfWindow,
              })}
            </div>
            <div style={{ padding: '0 14px 14px' }}>
              <UsageBreakdown usage={usage} stats={stats} />
            </div>
            <button
              type="button"
              className="mobile-action-sheet__row mobile-action-sheet__row--cancel"
              onClick={() => setOpen(false)}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** Shared body of the desktop popover and the mobile bottom-sheet:
 *  progress bar + per-bucket rows + session totals + footer hint. */
function UsageBreakdown({
  usage,
  stats,
}: {
  usage: ThreadUsage;
  stats: UsageStats;
}) {
  const { t } = useTranslation();
  const {
    window,
    sys,
    tools,
    postMsgs,
    preMsgs,
    actualInput,
    pctOfWindow,
    thresholdAsTotal,
    thresholdPct,
    accent,
  } = stats;
  return (
    <>
      <div
        className="mono"
        style={{
          fontSize: 9.5,
          color: 'var(--ink-3)',
          letterSpacing: '.1em',
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        {t('usage.contextWindow')}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 14,
            color: 'var(--ink-1)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatTokens(actualInput)} / {formatTokens(window)}
        </span>
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: accent,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {pctOfWindow}%
        </span>
      </div>
      {/* Progress bar with compaction threshold tick. */}
      <div
        aria-hidden
        style={{
          position: 'relative',
          height: 5,
          background: 'var(--rule)',
          borderRadius: 2,
          overflow: 'visible',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            width: `${pctOfWindow}%`,
            height: '100%',
            background: accent,
            borderRadius: 2,
            transition: 'width 240ms ease-out',
          }}
        />
        {thresholdPct !== null && thresholdPct < 100 && (
          <div
            title={t('usage.autoCompactionAt', {
              tokens: formatTokens(thresholdAsTotal),
              pct: thresholdPct,
            })}
            style={{
              position: 'absolute',
              left: `${thresholdPct}%`,
              top: -2,
              width: 2,
              height: 9,
              background: '#c97a16',
              transform: 'translateX(-1px)',
            }}
          />
        )}
      </div>

      <div
        className="mono"
        style={{
          fontSize: 9.5,
          color: 'var(--ink-3)',
          letterSpacing: '.1em',
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        {t('usage.breakdownNextTurn')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <UsageRow label={t('usage.systemPrompt')} value={sys} />
        <UsageRow label={t('usage.toolsSchema')} value={tools} />
        {usage.compact_triggered ? (
          <>
            <UsageRow
              label={t('usage.messagesCompacted', {
                count: usage.compacted_message_count,
              })}
              value={postMsgs}
            />
            <UsageRow label={t('usage.withoutCompaction')} value={preMsgs} muted />
          </>
        ) : (
          <UsageRow label={t('usage.messages')} value={postMsgs} />
        )}
        <UsageRow
          label={t('usage.freeSpace')}
          value={Math.max(0, window - actualInput)}
          muted
        />
      </div>

      <div
        className="mono"
        style={{
          fontSize: 9.5,
          color: 'var(--ink-3)',
          letterSpacing: '.1em',
          fontWeight: 700,
          marginTop: 12,
          marginBottom: 6,
        }}
      >
        {t('usage.sessionTotals')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <UsageRow label={t('usage.input')} value={usage.session_total_input} />
        <UsageRow label={t('usage.output')} value={usage.session_total_output} />
        <UsageRow label={t('usage.cacheRead')} value={usage.session_total_cache_read} />
        <UsageRow
          label={t('usage.cacheWrite')}
          value={usage.session_total_cache_creation}
        />
      </div>

      <div
        style={{
          fontSize: 10,
          color: 'var(--ink-3)',
          marginTop: 12,
          paddingTop: 8,
          borderTop: '1px solid var(--rule)',
          lineHeight: 1.5,
        }}
      >
        {usage.compact_triggered ? (
          <>
            <strong style={{ color: 'var(--ink-2)' }}>
              {t('usage.compactionActiveLabel')}
            </strong>{' '}
            {t('usage.compactionActiveBody', {
              count: usage.compacted_message_count,
            })}
          </>
        ) : (
          <>
            {t('usage.compactionHint', {
              tokens: formatTokens(thresholdAsTotal),
            })}
          </>
        )}
      </div>
    </>
  );
}

export function UsageRow({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: number;
  /** Render at lower contrast — used for "what compaction saved you"
   *  and free-space rows that are reference numbers, not real usage. */
  muted?: boolean;
}) {
  const labelColor = muted ? 'var(--ink-3)' : 'var(--ink-2)';
  const valueColor = muted ? 'var(--ink-3)' : 'var(--ink-1)';
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 11,
      }}
    >
      <span className="mono" style={{ color: labelColor, letterSpacing: '.04em' }}>
        {label}
      </span>
      <span
        className="mono"
        style={{
          color: valueColor,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value.toLocaleString()}
      </span>
    </div>
  );
}
